import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  delivered: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-700",
};

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="orders-panel">
      {orders.length === 0 && <p className="py-10 text-center text-sm text-emerald-950/60" data-testid="orders-empty">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o._id} className="rounded-2xl border border-amber-900/10 bg-white/80 p-5" data-testid={`order-row-${o.reference}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-display text-lg font-bold text-emerald-950">
                {o.reference} <span className={`ml-2 rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[o.status]}`} data-testid={`order-status-${o.reference}`}>{o.status}</span>
              </p>
              <p className="mt-1 text-sm font-semibold text-emerald-950/80">{o.customer_name} · {o.phone}</p>
              <p className="text-sm text-emerald-950/60">
                {o.item_name} × {o.quantity}{o.extra_roti_qty > 0 ? ` + ${o.extra_roti_qty} extra roti` : ""} → {o.station_name} ({o.station_code})
                {o.pnr ? ` · PNR ${o.pnr}` : ""}{o.coach || o.seat ? ` · ${o.coach} ${o.seat}` : ""}
              </p>
              <p className="mt-1 text-xs text-emerald-950/50">{new Date(o.created_at).toLocaleString("en-IN")}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-xl font-bold text-emerald-950" data-testid={`order-total-${o.reference}`}>₹{o.total}</p>
              <Select value={o.status} onValueChange={(v) => setStatus(o._id, v)}>
                <SelectTrigger className="mt-2 h-9 w-36 rounded-xl text-xs font-bold" data-testid={`order-status-select-${o.reference}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_COLORS).map((s) => (
                    <SelectItem key={s} value={s} data-testid={`order-status-option-${o.reference}-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_STATION = { name: "", code: "", delivery_charge: 100, cutoff_minutes: 60, delivery_available: true, active: true };

function StationsPanel() {
  const [stations, setStations] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newStation, setNewStation] = useState(EMPTY_STATION);

  const load = useCallback(() => api.get("/admin/stations").then((r) => {
    setStations(r.data);
    setDrafts(Object.fromEntries(r.data.map((s) => [s._id, { ...s }])));
  }).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async (id) => {
    const d = drafts[id];
    try {
      await api.put(`/admin/stations/${id}`, {
        name: d.name, code: d.code, delivery_available: !!d.delivery_available,
        delivery_charge: Number(d.delivery_charge), cutoff_minutes: Number(d.cutoff_minutes), active: !!d.active,
      });
      toast.success("Station saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stations/${id}`);
      toast.success("Station deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stations", {
        ...newStation,
        code: newStation.code.toUpperCase(),
        delivery_charge: Number(newStation.delivery_charge),
        cutoff_minutes: Number(newStation.cutoff_minutes),
      });
      toast.success("Station added");
      setNewStation(EMPTY_STATION);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const field = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  return (
    <div className="space-y-4" data-testid="stations-panel">
      <form onSubmit={add} className="grid gap-3 rounded-2xl border border-dashed border-emerald-900/25 bg-emerald-50/50 p-5 sm:grid-cols-[1fr_110px_120px_120px_auto]" data-testid="station-add-form">
        <input required value={newStation.name} onChange={(e) => setNewStation((s) => ({ ...s, name: e.target.value }))} placeholder="New station name" data-testid="station-add-name" className="h-11 rounded-xl border border-emerald-900/15 bg-white px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
        <input required value={newStation.code} onChange={(e) => setNewStation((s) => ({ ...s, code: e.target.value.toUpperCase().slice(0, 6) }))} placeholder="CODE" data-testid="station-add-code" className="h-11 rounded-xl border border-emerald-900/15 bg-white px-3 text-sm font-semibold uppercase focus:outline-none focus:ring-2 focus:ring-amber-500" />
        <input required type="number" min="0" value={newStation.delivery_charge} onChange={(e) => setNewStation((s) => ({ ...s, delivery_charge: e.target.value }))} placeholder="Delivery ₹" data-testid="station-add-charge" className="h-11 rounded-xl border border-emerald-900/15 bg-white px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
        <input required type="number" min="0" value={newStation.cutoff_minutes} onChange={(e) => setNewStation((s) => ({ ...s, cutoff_minutes: e.target.value }))} placeholder="Cutoff min" data-testid="station-add-cutoff" className="h-11 rounded-xl border border-emerald-900/15 bg-white px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
        <button type="submit" data-testid="station-add-button" className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-900 px-5 text-sm font-bold text-amber-50 hover:bg-emerald-800">
          <Plus className="h-4 w-4" /> Add
        </button>
      </form>

      {stations.map((s) => {
        const d = drafts[s._id] || s;
        return (
          <div key={s._id} className="grid items-center gap-3 rounded-2xl border border-amber-900/10 bg-white/80 p-5 lg:grid-cols-[1fr_90px_110px_110px_repeat(3,auto)]" data-testid={`station-row-${s.code}`}>
            <input value={d.name} onChange={(e) => field(s._id, "name", e.target.value)} data-testid={`station-name-${s.code}`} className="h-11 rounded-xl border border-emerald-900/15 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <span className="text-sm font-bold tracking-widest text-emerald-900/60">{s.code}</span>
            <input type="number" min="0" value={d.delivery_charge} onChange={(e) => field(s._id, "delivery_charge", e.target.value)} data-testid={`station-charge-input-${s.code}`} className="h-11 rounded-xl border border-emerald-900/15 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <input type="number" min="0" value={d.cutoff_minutes} onChange={(e) => field(s._id, "cutoff_minutes", e.target.value)} data-testid={`station-cutoff-input-${s.code}`} className="h-11 rounded-xl border border-emerald-900/15 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <label className="flex items-center gap-2 text-xs font-bold text-emerald-950/70">
              <input type="checkbox" checked={!!d.delivery_available} onChange={(e) => field(s._id, "delivery_available", e.target.checked)} data-testid={`station-available-${s.code}`} className="h-4 w-4 accent-emerald-800" />
              Delivering
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-emerald-950/70">
              <input type="checkbox" checked={!!d.active} onChange={(e) => field(s._id, "active", e.target.checked)} data-testid={`station-active-${s.code}`} className="h-4 w-4 accent-emerald-800" />
              Listed
            </label>
            <div className="flex gap-2">
              <button onClick={() => save(s._id)} data-testid={`station-save-${s.code}`} className="h-10 rounded-xl bg-amber-600 px-4 text-sm font-bold text-amber-50 hover:bg-amber-700">Save</button>
              <button onClick={() => remove(s._id)} data-testid={`station-delete-${s.code}`} aria-label={`Delete ${s.name}`} className="grid h-10 w-10 place-items-center rounded-xl border border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MenuPanel() {
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  const load = useCallback(() => api.get("/admin/menu").then((r) => {
    setItems(r.data);
    setPrices(Object.fromEntries(r.data.map((i) => [i._id, i.price])));
  }).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async (item, active) => {
    try {
      await api.put(`/admin/menu/${item._id}`, { price: Number(prices[item._id]), active });
      toast.success("Menu updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="menu-panel">
      {items.map((item) => (
        <div key={item._id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-900/10 bg-white/80 p-5" data-testid={`menu-row-${item._id}`}>
          <div>
            <p className="font-display text-lg font-bold text-emerald-950">{item.name}</p>
            <p className="text-sm text-emerald-950/60">{(item.contents || []).join(" · ")}</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-bold text-emerald-950/70">
              <input type="checkbox" checked={!!item.active} onChange={(e) => save(item, e.target.checked)} data-testid={`menu-active-${item._id}`} className="h-4 w-4 accent-emerald-800" />
              On menu
            </label>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-emerald-950">₹</span>
              <input type="number" min="0" value={prices[item._id] ?? item.price} onChange={(e) => setPrices((p) => ({ ...p, [item._id]: e.target.value }))} data-testid={`menu-price-input-${item._id}`} className="h-11 w-28 rounded-xl border border-emerald-900/15 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <button onClick={() => save(item, item.active)} data-testid={`menu-save-${item._id}`} className="h-11 rounded-xl bg-amber-600 px-5 text-sm font-bold text-amber-50 hover:bg-amber-700">Save</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-amber-900/10 bg-[#FFFBEB]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm font-semibold text-emerald-950/70 sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-900/20 px-4 py-2 text-sm font-bold text-emerald-900 transition-colors hover:bg-emerald-900 hover:text-amber-50"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <h1 className="font-display text-3xl font-bold text-emerald-950 sm:text-4xl" data-testid="admin-heading">Dashboard</h1>
        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="rounded-full bg-emerald-900/5 p-1" data-testid="admin-tabs">
            <TabsTrigger value="orders" className="rounded-full px-5" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="stations" className="rounded-full px-5" data-testid="tab-stations">Stations</TabsTrigger>
            <TabsTrigger value="menu" className="rounded-full px-5" data-testid="tab-menu">Menu</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-6"><OrdersPanel /></TabsContent>
          <TabsContent value="stations" className="mt-6"><StationsPanel /></TabsContent>
          <TabsContent value="menu" className="mt-6"><MenuPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
